/**
 * Help (the app's Settings › Help panel, HomeScene "Help" + HelpPanelHome /
 * HelpElement): "Follow us for tips and tutorials" with Facebook, Instagram,
 * YouTube and the web, and the frequently asked questions, each one opening
 * and closing under its title (several can be open at once), in the app's
 * order. The questions about sharing, codes, results, accounts and classrooms
 * describe the web (HELP_WEB); the rest are the app's own texts
 * (Resources/Xml, help<N>Title / help<N>), and Hebrew, which the app does not
 * have, comes from HELP_HE.
 */
import { useEffect, useState } from "react";
import { loadAppTexts, ut } from "../editor/appText";
import { currentLanguage, t } from "../player/i18n";
import { RTL_LANGUAGES } from "../player/rtl";
import { LINKS } from "./Landing";
import { HELP_WEB } from "./helpWeb";
import { Icon } from "./widgets";

/** The order of the questions in the panel (share, getCode, QuizzesTests, whatAreGames, checkResults, limitations, advantages, createAccount, studentsPay, classrooms). */
const QUESTIONS = [8, 9, 1, 2, 3, 4, 5, 6, 7, 10];

const SOCIAL = [
  { icon: "facebook", label: "Facebook", href: LINKS.facebook },
  { icon: "instagram", label: "Instagram", href: LINKS.instagram },
  { icon: "youtube", label: "YouTube", href: LINKS.youtube },
  { icon: "web", label: "makeit.app", href: "https://makeit.app" },
];

export function HelpView() {
  const [ready, setReady] = useState(false);
  const [open, setOpen] = useState<Set<number>>(() => new Set());
  useEffect(() => {
    let gone = false;
    void loadAppTexts().then(() => !gone && setReady(true));
    return () => {
      gone = true;
    };
  }, []);
  const lang = currentLanguage();
  const he = lang === "he";
  const text = (id: string) => HELP_WEB[lang]?.[id] || (he && HELP_HE[id]) || ut(id);
  const toggle = (n: number) =>
    setOpen((cur) => {
      const next = new Set(cur);
      if (!next.delete(n)) next.add(n);
      return next;
    });

  return (
    <section className="view help-view" dir={RTL_LANGUAGES.has(lang) ? "rtl" : undefined}>
      <h1>{t("help")}</h1>
      {ready && (
        <>
          <div className="help-follow">
            <span>{text("helpXXSS")}</span>
            <span className="help-social">
              {SOCIAL.map((s) => (
                <a key={s.icon} href={s.href} target="_blank" rel="noreferrer" aria-label={s.label} title={s.label} className={`help-${s.icon}`}>
                  <Icon name={s.icon} />
                </a>
              ))}
            </span>
          </div>
          <div className="panel-list help-list">
            {QUESTIONS.map((n) => {
              const on = open.has(n);
              return (
                <div key={n} className={`help-item${on ? " open" : ""}`}>
                  <button className="help-q" aria-expanded={on} aria-controls={`help-a-${n}`} onClick={() => toggle(n)}>
                    <span>{text(`help${n}Title`)}</span>
                    <Icon name="link" className="help-arrow" />
                  </button>
                  <div className="help-a" id={`help-a-${n}`} role="region" aria-hidden={!on}>
                    <div>
                      <p>{text(`help${n}`).trim()}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
}

/** The help texts in Hebrew (the app's XML has no Hebrew). */
const HELP_HE: Record<string, string> = {
  helpXXSS: "עקבו אחרינו לטיפים ולמדריכים",
  help8Title: "איך משתפים פרויקט?",
  help9Title: "איך מקבלים את הקוד לשיתוף פרויקט?",
  help1Title: "מה הם מבחנים/חידונים?",
  help2Title: "מה הם משחקים?",
  help2:
    "המשחקים מיועדים לילדים צעירים מאוד, גם לכאלה שעדיין אינם קוראים. כברירת מחדל ניתנת עדיפות לחיזוק חיובי ואין עונש על טעויות, כך שהמשתתפים יכולים לנסות שוב ושוב עד שיענו נכון.\nבמשחקים המשתתפים אינם מזינים את שמם כדי לשחק.\nאפשר לראות את התוצאות והסטטיסטיקות של המשחקים רק אם המשחק משותף דרך כיתה (לא באמצעות קוד או קישור).\nהמשחקים הם משאב מצוין לילדים צעירים.",
  help3Title: "איך רואים את התוצאות והסטטיסטיקות של המשחקים?",
  help4Title: "מהן המגבלות של הגרסה החינמית?",
  help4: "הגרסה החינמית מאפשרת להשתמש כמעט בכל התכונות של Make It, אבל מאפשרת ליצור רק 2 פרויקטים, כיתה אחת ותיקייה אחת.",
  help5Title: "מה היתרונות של המינוי ל-Make It?",
  help5:
    "עם המינוי אפשר להשתמש ב-Make It בלי אף אחת מהמגבלות של הגרסה החינמית. אפשר ליצור ולשתף כמה פרויקטים שרוצים, וגם ליצור כיתות ותיקיות.\nאפשר גם לראות את התוצאות והסטטיסטיקות של כל התלמידים.",
  help6Title: "למה כדאי ליצור חשבון?",
  help7Title: "האם התלמידים צריכים מינוי כדי להשתמש ב-Make It?",
  help7: "לא, התלמידים משתמשים באפליקציה בחינם, בלי מינוי.",
  help10Title: "מה הן כיתות ואיך הן עובדות?",
};
